import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  UpsertSchedulerGroupInput,
  useCreateSchedulerGroupMutation,
  useDeleteSchedulerGroupMutation,
  useGetSchedulerGroupsQuery,
  useSchedulerReferenceUsers,
  useUpdateSchedulerGroupMutation,
} from '@/app/services/serviceScheduler';
import { addToast } from '@/app/slices/toast';
import { ChipSelector } from '@/components/common';
import {
  Button,
  InputText,
} from '@/components/common/service-scheduler/compat-controls';
import {
  SettingsItem,
  SettingsList,
} from '@/components/common/service-scheduler/settings-list';

export default function GroupSettingsPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const {
    data: groups = [],
    isLoading: isLoadingGroups,
    error: groupsError,
  } = useGetSchedulerGroupsQuery();
  const { users: referenceUsers } = useSchedulerReferenceUsers({
    activeOnly: true,
    startRow: 1,
    endRow: 1000,
  });
  const [createGroup, { isLoading: isCreating }] =
    useCreateSchedulerGroupMutation();
  const [updateGroup, { isLoading: isUpdatingGroup }] =
    useUpdateSchedulerGroupMutation();
  const [deleteGroup] = useDeleteSchedulerGroupMutation();

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(query.toLowerCase()),
  );
  const selectableUsers = referenceUsers
    .filter((user) => user.isPse || user.isPsv || user.isTechnician)
    .map((user) => ({
      id: user.equipmentId,
      name: user.name,
    }));
  const userNameById = new Map(
    selectableUsers.map((user) => [user.id, user.name]),
  );

  const groupItems: SettingsItem[] = filteredGroups.map((group) => ({
    id: group.groupId,
    title: group.name,
    icon: 'pi-users',
    content: (
      <div className="space-y-2 pt-2">
        <ChipSelector
          value={group.userIds}
          onChange={async (userIds) => {
            try {
              await updateGroup({
                groupId: group.groupId,
                input: {
                  name: group.name,
                  userIds,
                },
              }).unwrap();
              dispatch(
                addToast({
                  severity: 'success',
                  summary: t('common.save'),
                  detail: t('common.save'),
                }),
              );
            } catch {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: t('common.error'),
                  detail: t('common.error'),
                  sticky: true,
                }),
              );
            }
          }}
          options={selectableUsers}
          optionId="id"
          optionLabel="name"
          filterBy={['name', 'id']}
          maxSelectedLabels={3}
          placeholder={t('serviceScheduler.psePsv')}
          className="w-full"
          loading={isUpdatingGroup}
        />
        {group.userIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {group.userIds.map((userId) => (
              <button
                key={userId}
                type="button"
                className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                onClick={async () => {
                  const nextUserIds = group.userIds.filter(
                    (id) => id !== userId,
                  );
                  try {
                    await updateGroup({
                      groupId: group.groupId,
                      input: {
                        name: group.name,
                        userIds: nextUserIds,
                      },
                    }).unwrap();
                    dispatch(
                      addToast({
                        severity: 'success',
                        summary: t('common.save'),
                        detail: t('common.save'),
                      }),
                    );
                  } catch {
                    dispatch(
                      addToast({
                        severity: 'error',
                        summary: t('common.error'),
                        detail: t('common.error'),
                        sticky: true,
                      }),
                    );
                  }
                }}
              >
                <span className="max-w-[180px] truncate">
                  {userNameById.get(userId) ?? userId}
                </span>
                <span className="font-semibold">×</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-end">
          <Button
            label={t('common.delete')}
            severity="danger"
            size="small"
            onClick={async () => {
              try {
                await deleteGroup({ groupId: group.groupId }).unwrap();
                dispatch(
                  addToast({
                    severity: 'success',
                    summary: t('common.delete'),
                    detail: t('common.delete'),
                  }),
                );
              } catch {
                dispatch(
                  addToast({
                    severity: 'error',
                    summary: t('common.error'),
                    detail: t('common.error'),
                    sticky: true,
                  }),
                );
              }
            }}
          />
        </div>
      </div>
    ),
  }));

  const createItem: SettingsItem = {
    id: 'create-group',
    title: t('common.create'),
    icon: 'pi-plus',
    content: (
      <div className="space-y-2 py-2">
        <InputText
          placeholder={t('common.name')}
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          className="w-full"
        />
        <div className="flex justify-end">
          <Button
            label={t('common.create')}
            size="small"
            loading={isCreating}
            disabled={!newGroupName.trim() || isCreating}
            onClick={async () => {
              const input: UpsertSchedulerGroupInput = {
                name: newGroupName.trim(),
                userIds: [],
              };
              try {
                await createGroup(input).unwrap();
                setNewGroupName('');
                dispatch(
                  addToast({
                    severity: 'success',
                    summary: t('common.create'),
                    detail: t('common.create'),
                  }),
                );
              } catch {
                dispatch(
                  addToast({
                    severity: 'error',
                    summary: t('common.error'),
                    detail: t('common.error'),
                    sticky: true,
                  }),
                );
              }
            }}
          />
        </div>
      </div>
    ),
  };

  const itemsToRender = [...groupItems, createItem];

  if (groupsError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('common.error')}: Failed to load groups.
      </div>
    );
  }

  if (isLoadingGroups) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <>
      <span className="p-input-icon-right mb-2 w-full">
        <InputText
          placeholder={t('common.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full"
        />
      </span>

      {groupItems.length === 0 && query === '' ? (
        <div className="py-4 text-center text-sm text-gray-500">
          No groups yet. Create one below.
        </div>
      ) : null}

      <SettingsList items={itemsToRender} multiple={false} />
    </>
  );
}
